/**
 * Sync-Krypto – alles, was den Gerät verlässt, wird hier verschlüsselt.
 *
 * Wurzel ist das Bereichsgeheimnis (32 Byte Zufall, steckt im Kopplungscode).
 * Daraus leitet HKDF drei Schlüssel ab:
 *   - die Bereichs-ID, unter der der Server ablegt,
 *   - den Inhaltsschlüssel (AES-GCM) für Zustand und Aufnahmen,
 *   - den Kennungsschlüssel (HMAC) für Audio-Kennungen, damit der Server vom
 *     Kennungswert nicht auf den Text schließen kann.
 */

const encoder = new TextEncoder();
const IV_BYTES = 12;

/**
 * @typedef {Object} SpaceKeys
 * @property {string} spaceId - 64 Hex-Zeichen
 * @property {CryptoKey} contentKey - AES-GCM
 * @property {CryptoKey} idKey - HMAC-SHA-256
 */

/**
 * Erzeugt ein neues Bereichsgeheimnis
 * @returns {Uint8Array}
 */
export function generateSecret() {
  return crypto.getRandomValues(new Uint8Array(32));
}

/**
 * Leitet die Schlüssel eines Bereichs aus seinem Geheimnis ab
 * @param {Uint8Array} secret
 * @returns {Promise<SpaceKeys>}
 */
export async function deriveSpaceKeys(secret) {
  const root = await crypto.subtle.importKey('raw', secret, 'HKDF', false, [
    'deriveBits',
    'deriveKey',
  ]);
  const hkdf = (info) => ({
    name: 'HKDF',
    hash: 'SHA-256',
    salt: new Uint8Array(),
    info: encoder.encode(`the-reader-sync/${info}`),
  });

  const spaceIdBits = await crypto.subtle.deriveBits(hkdf('space-id'), root, 256);
  const contentKey = await crypto.subtle.deriveKey(
    hkdf('content'),
    root,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
  const idKey = await crypto.subtle.deriveKey(
    hkdf('audio-id'),
    root,
    { name: 'HMAC', hash: 'SHA-256', length: 256 },
    false,
    ['sign']
  );
  return { spaceId: toHex(spaceIdBits), contentKey, idKey };
}

/**
 * Verschlüsselt Bytes; das Ergebnis beginnt mit dem zufälligen IV
 * @param {CryptoKey} key
 * @param {Uint8Array} plain
 * @returns {Promise<Uint8Array>}
 */
export async function encrypt(key, plain) {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain);
  const out = new Uint8Array(IV_BYTES + cipher.byteLength);
  out.set(iv);
  out.set(new Uint8Array(cipher), IV_BYTES);
  return out;
}

/**
 * Entschlüsselt, was encrypt erzeugt hat. Wirft bei falschem Schlüssel oder
 * veränderten Daten.
 * @param {CryptoKey} key
 * @param {Uint8Array} data
 * @returns {Promise<Uint8Array>}
 */
export async function decrypt(key, data) {
  const iv = data.subarray(0, IV_BYTES);
  const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data.subarray(IV_BYTES));
  return new Uint8Array(plain);
}

/**
 * Bildet die Server-Kennung einer Aufnahme aus ihrem lokalen Cache-Key
 * @param {CryptoKey} idKey
 * @param {string} cacheKey
 * @returns {Promise<string>} 64 Hex-Zeichen
 */
export async function audioId(idKey, cacheKey) {
  return toHex(await crypto.subtle.sign('HMAC', idKey, encoder.encode(cacheKey)));
}

/**
 * Packt ein Objekt als komprimiertes JSON – der Zustand enthält ganze Texte
 * @param {unknown} value
 * @returns {Promise<Uint8Array>}
 */
export async function packJson(value) {
  const stream = new Response(JSON.stringify(value)).body.pipeThrough(
    new CompressionStream('gzip')
  );
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Gegenstück zu packJson
 * @param {Uint8Array} data
 * @returns {Promise<unknown>}
 */
export async function unpackJson(data) {
  const stream = new Response(data).body.pipeThrough(new DecompressionStream('gzip'));
  return JSON.parse(await new Response(stream).text());
}

/** @param {ArrayBuffer|Uint8Array} bytes */
function toHex(bytes) {
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** @param {Uint8Array} bytes */
export function toBase64Url(bytes) {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/** @param {string} text */
export function fromBase64Url(text) {
  const base64 = text.replace(/-/g, '+').replace(/_/g, '/');
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}
